import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import NewsCard from '../components/NewsCard';
import AICard from '../components/AICard';
import FilterSidebar from '../components/FilterSidebarWeekly';
import Navbar from '../components/NavbarWeekly';
import InfoBanner from '../components/InfoBanner';
import BulkShareModal from '../components/BulkShareModal';
import TimelinePanel from '../components/TimelinePanel';
import { fetchNewsletterData, transformRecordToProps, addLike, getLikeList, likesToMap, getStoryline } from '../utils/newbackendcall';
import { useUser } from '../../../../context/UserContext';
import '../styles/newsletter.css';
import useAuth from '../../../../hooks/useAuth';

// Helper functions defined before useState to avoid temporal dead zone
const getToday = () => {
  const d = new Date();
  return d.toISOString().split('T')[0];
};

const getLastMonthStart = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  d.setDate(1);
  return d.toISOString().split('T')[0];
};

const NewsletterPage = () => {
  useAuth();
  const { user } = useUser();
  const [startDate, setStartDate] = useState(() => getToday());
  const [endDate, setEndDate] = useState(() => getLastMonthStart());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNewsTypes, setSelectedNewsTypes] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [allRecords, setAllRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [selectedArticleIds, setSelectedArticleIds] = useState(new Set());
  const [isBulkShareModalOpen, setIsBulkShareModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState('publish_date');
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(true);
  const ITEMS_PER_PAGE = 50;
  const [userVotes, setUserVotes] = useState(new Map());
  const [votingArticleId, setVotingArticleId] = useState(null);
  const [testRawData, setTestRawData] = useState(null);
  const [isTestLoading, setIsTestLoading] = useState(false);
  const loadMoreRef = useRef(null);
  const [companiesReady, setCompaniesReady] = useState(false);
  const isLoadingRef = useRef(false);
  const loadMoreTimeoutRef = useRef(null);

  // Track allowed company codes (from user's competitor list)
  const [allowedCompanyCodes, setAllowedCompanyCodes] = useState([]);

  // Track company names from card clicks for immediate sidebar display
  const [clickedCompanyNames, setClickedCompanyNames] = useState({});

  // Track company names map from sidebar (for display in active filter bar)
  const [sidebarCompanyMap, setSidebarCompanyMap] = useState({});

  const [onlyWithTimeline, setOnlyWithTimeline] = useState(false);

  // Timeline state at page level
  const [isTimelinePanelOpen, setIsTimelinePanelOpen] = useState(false);
  const [timelineArticleTitle, setTimelineArticleTitle] = useState('');
  const [timelineArticleId, setTimelineArticleId] = useState('');
  const [timelineArticleUrl, setTimelineArticleUrl] = useState('');
  const [storylinesData, setStorylinesData] = useState([]);

  const handleOpenTimeline = useCallback((articleId, articleTitle, articleUrl) => {
    setTimelineArticleId(articleId);
    setTimelineArticleTitle(articleTitle);
    setTimelineArticleUrl(articleUrl);
    setIsTimelinePanelOpen(true);
  }, []);

  const handleCloseTimeline = useCallback(() => {
    setIsTimelinePanelOpen(false);
    setTimelineArticleId('');
    setTimelineArticleTitle('');
    setTimelineArticleUrl('');
  }, []);

  // Fetch storylines data once on mount
  useEffect(() => {
    const fetchStorylines = async () => {
      try {
        const data = await getStoryline();
        setStorylinesData(data?.storylines || data || []);
      } catch (err) {
        console.error('Failed to fetch storylines:', err);
      }
    };
    fetchStorylines();
  }, []);

  // News types and regions mappings for active filters display
  const newsTypesMap = useMemo(() => ({
    "government_and_regulatory_news": "Government and Regulatory News",
    "new_product_and_project_updates": "New Product & Project Updates",
    "marketing_initiatives": "Marketing Initiatives",
    "other_company_news": "Other Company News",
    "financial_announcements": "Financial Announcements",
    "mergers_and_acquisitions": "Mergers & Acquisitions",
    "contract": "Contract",
    "investment_and_capital_raising": "Investment & Capital Raising",
    "research_and_development": "Research & Development",
    "exhibitions_and_conferences": "Exhibitions & Conferences",
    "joint_ventures_collaborations_partnerships": "Joint Ventures Collaborations Partnerships"
  }), []);

  // Create reverse maps for lookup (Name -> Code)
  const newsTypesReverseMap = useMemo(() => {
    return Object.entries(newsTypesMap).reduce((acc, [key, value]) => {
      acc[value] = key;
      return acc;
    }, {});
  }, [newsTypesMap]);

  const regionsMap = useMemo(() => ({
    "AP": "Asia-Pacific",
    "NM": "North America",
    "EU": "Europe",
    "MA": "Middle East & Africa",
    "SA": "South America"
  }), []);

  const regionsReverseMap = useMemo(() => {
    return Object.entries(regionsMap).reduce((acc, [key, value]) => {
      acc[value] = key;
      return acc;
    }, {});
  }, [regionsMap]);

  // Handle removing individual filter
  const handleRemoveFilter = useCallback((filterType, value) => {
    if (filterType === 'search') {
      setSearchQuery('');
    } else if (filterType === 'company') {
      setSelectedCompanies(prev => prev.filter(c => c !== value));
    } else if (filterType === 'newsType') {
      setSelectedNewsTypes(prev => prev.filter(nt => nt !== value));
    } else if (filterType === 'region') {
      setSelectedRegions(prev => prev.filter(r => r !== value));
    } else if (filterType === 'timeline') {
      setOnlyWithTimeline(false);
    }
  }, []);

  // Handle reset all filters
  const handleResetAllFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedCompanies([]);
    setSelectedNewsTypes([]);
    setSelectedRegions([]);
    setOnlyWithTimeline(false);
  }, []);

  // const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  // Handler called by FilterSidebar when company codes are ready
  const handleCompaniesReady = useCallback((codes) => {
    setAllowedCompanyCodes(codes); // Store the allowed company codes
    setCompaniesReady(true);
  }, []);

  const getFilterData = useCallback(() => ({
    labels: [],
    newstypes: selectedNewsTypes,
    regions: selectedRegions,
    companies: selectedCompanies.length > 0 ? selectedCompanies : allowedCompanyCodes,
    dates: [endDate, startDate]
  }), [selectedNewsTypes, selectedRegions, selectedCompanies, startDate, endDate, allowedCompanyCodes]);

  useEffect(() => {
    const fetchUserLikes = async () => {
      try {
        const likes = await getLikeList();
        const likesMap = likesToMap(likes);
        setUserVotes(likesMap);
      } catch (err) {
        console.error('Failed to fetch user likes:', err);
      }
    };
    fetchUserLikes();
  }, []);

  useEffect(() => {
    // Don't fetch newsletter data until company codes are ready
    if (!companiesReady) {
      return;
    }

    const loadInitialData = async () => {
      setIsLoading(true);
      setError(null);
      setCurrentPage(0);
      setAllRecords([]);
      setHasMoreData(true);
      try {
        const data = await fetchNewsletterData({
          filterdata: getFilterData(),
          sortvalue: sortBy === 'publish_date' ? 'published_date' : 'sri_rank_value',
          skip: 0,
          limit: ITEMS_PER_PAGE,
          searchterm: searchQuery
        });
        setAllRecords(data);
        setHasMoreData(data.length === ITEMS_PER_PAGE);
      } catch (err) {
        console.error('Failed to fetch newsletter data:', err);
        setError('Failed to load newsletter data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    // Add a small delay to allow the UI (checkboxes) to update visually 
    // before triggering the expensive data fetch and loading state
    const timerId = setTimeout(() => {
      loadInitialData();
    }, 50);

    return () => clearTimeout(timerId);
  }, [companiesReady, startDate, endDate, searchQuery, selectedNewsTypes, selectedRegions, selectedCompanies, getFilterData, sortBy]);

  const loadMoreData = useCallback(async () => {
    // Use ref-based guard to prevent concurrent fetches
    if (isLoadingRef.current || !hasMoreData) return;
    isLoadingRef.current = true;
    setIsLoadingMore(true);
    const nextPage = currentPage + 1;
    try {
      const data = await fetchNewsletterData({
        filterdata: getFilterData(),
        sortvalue: sortBy === 'publish_date' ? 'published_date' : 'sri_rank_value',
        skip: nextPage * ITEMS_PER_PAGE,
        limit: ITEMS_PER_PAGE,
        searchterm: searchQuery
      });
      // Use requestAnimationFrame to batch DOM updates and reduce reflow
      requestAnimationFrame(() => {
        setAllRecords(prev => {
          if (data.length === 0) return prev;
          // Optimized merging using Map for O(1) lookups
          const existingMap = new Map(prev.map(r => [r._id, r]));
          const newRecords = data.filter(r => !existingMap.has(r._id));
          if (newRecords.length === 0) return prev;
          return [...prev, ...newRecords];
        });
        setCurrentPage(nextPage);
        setHasMoreData(data.length === ITEMS_PER_PAGE);
      });
    } catch (err) {
      console.error('Failed to load more data:', err);
    } finally {
      isLoadingRef.current = false;
      setIsLoadingMore(false);
    }
  }, [currentPage, hasMoreData, getFilterData, searchQuery, sortBy]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasMoreData && !isLoading && !isLoadingMore) {
          // Debounce the load more call to prevent rapid firing
          if (loadMoreTimeoutRef.current) {
            clearTimeout(loadMoreTimeoutRef.current);
          }
          loadMoreTimeoutRef.current = setTimeout(() => {
            loadMoreData();
          }, 150);
        }
      },
      { threshold: 0.5, rootMargin: '200px' }
    );
    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      // Cleanup timeout on unmount
      if (loadMoreTimeoutRef.current) {
        clearTimeout(loadMoreTimeoutRef.current);
      }
    };
  }, [hasMoreData, isLoading, isLoadingMore, loadMoreData]);

  const handleVote = useCallback(async (articleId, voteType) => {
    if (votingArticleId) return;
    setVotingArticleId(articleId);
    try {
      await addLike({
        isLiked: voteType,
        entityCode: articleId,
      });
      setUserVotes(prev => {
        const newVotes = new Map(prev);
        if (voteType === 0) {
          newVotes.delete(articleId);
        } else {
          newVotes.set(articleId, voteType);
        }
        return newVotes;
      });
    } catch (err) {
      console.error('Failed to submit vote:', err);
    } finally {
      setVotingArticleId(null);
    }
  }, [votingArticleId]);

  const timelineArticleIds = useMemo(() => {
    const ids = new Set();
    if (storylinesData?.length) {
      storylinesData.forEach(sl => {
        sl?.timelines?.forEach(t => {
          const eventId = t?.event_id?.$oid || t?.event_id;
          if (eventId) ids.add(String(eventId));
        });
      });
    }
    return ids;
  }, [storylinesData]);

  const transformedArticles = useMemo(() => {
    let articles = allRecords.map(record => transformRecordToProps(record));
    
    if (onlyWithTimeline) {
      articles = articles.filter(a => timelineArticleIds.has(String(a.id)));
    }

    if (sortBy === 'importance') {
      return articles.sort((a, b) => (b.sriRankValue || 0) - (a.sriRankValue || 0));
    } else if (sortBy === 'publish_date') {
      return articles.sort((a, b) => {
        const dateA = a.publishedDate ? new Date(a.publishedDate) : new Date(0);
        const dateB = b.publishedDate ? new Date(b.publishedDate) : new Date(0);
        return dateB - dateA;
      });
    }
    return articles;
  }, [allRecords, sortBy, onlyWithTimeline, timelineArticleIds]);

  // Company names map (code to name) - built from transformed articles
  const companyNamesMap = useMemo(() => {
    const map = {};
    transformedArticles.forEach(article => {
      article.companies.forEach(company => {
        const code = company.code || company.name || company;
        const name = company.name || company;
        if (code && name) {
          map[code] = name;
        }
      });
    });
    return map;
  }, [transformedArticles]);

  // Combined company names map: merges names from articles + clicked companies + sidebar map
  const combinedCompanyNamesMap = useMemo(() => {
    return { ...companyNamesMap, ...clickedCompanyNames, ...sidebarCompanyMap };
  }, [companyNamesMap, clickedCompanyNames, sidebarCompanyMap]);

  // Handle filter click from cards (defined after companyNamesMap to avoid temporal dead zone)
  const handleFilterClick = useCallback((filterType, value) => {
    if (filterType === 'company') {
      // Extract code and name from company object
      const companyCode = typeof value === 'object' ? (value.code || value.name || '') : value;
      const companyName = typeof value === 'object' ? (value.name || value.code || value) : value;

      // Validate if this company is in the allowed list
      if (!allowedCompanyCodes.includes(companyCode)) {
        // Company is not in the user's competitor list
        alert(`To view news related to ${companyName}, please write to growthiq@marketsandmarkets.com`);
        return; // Don't add to filters
      }

      // Use flushSync to immediately update the DOM before async operations
      flushSync(() => {
        // Store the company name for immediate sidebar display
        if (companyCode && companyName && companyCode !== companyName) {
          setClickedCompanyNames(prev => ({
            ...prev,
            [companyCode]: companyName
          }));
        }

        // Add company code to filter if not already selected
        setSelectedCompanies(prev => {
          if (!prev.includes(companyCode)) {
            return [...prev, companyCode];
          }
          return prev;
        });
      });
    } else if (filterType === 'newsType') {
      // Use flushSync for instant checkbox update
      flushSync(() => {
        // Normalize value: If it's a name, convert to code
        const code = newsTypesReverseMap[value] || value;

        // Add news type to filter if not already selected
        setSelectedNewsTypes(prev => {
          if (!prev.includes(code)) {
            return [...prev, code];
          }
          return prev;
        });
      });
    } else if (filterType === 'region') {
      // Use flushSync for instant checkbox update
      flushSync(() => {
        // Normalize value: If it's a name, convert to code
        const code = regionsReverseMap[value] || value;

        // Add region to filter if not already selected
        setSelectedRegions(prev => {
          if (!prev.includes(code)) {
            return [...prev, code];
          }
          return prev;
        });
      });
    }
  }, [allowedCompanyCodes, companyNamesMap, combinedCompanyNamesMap, newsTypesReverseMap, regionsReverseMap]);

  const selectedArticles = useMemo(() => {
    return transformedArticles.filter(article => selectedArticleIds.has(article.id));
  }, [transformedArticles, selectedArticleIds]);

  const handleToggleSelection = (articleId) => {
    setSelectedArticleIds(prevSelected => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(articleId)) {
        newSelected.delete(articleId);
      } else {
        if (newSelected.size >= 5) {
          alert('You can select a maximum of 5 articles to share at once.');
          return prevSelected;
        }
        newSelected.add(articleId);
      }
      return newSelected;
    });
  };

  const handleClearSelection = () => {
    setSelectedArticleIds(new Set());
  };

  const handleBulkShare = () => {
    setIsBulkShareModalOpen(true);
  };

  const handleBulkShareClose = () => {
    setIsBulkShareModalOpen(false);
  };

  const handleTestFetch = async () => {
    setIsTestLoading(true);
    setTestRawData(null);
    const BATCH_SIZE = 50;
    try {
      const data = await fetchNewsletterData({
        filterdata: getFilterData(),
        sortvalue: sortBy === 'publish_date' ? 'published_date' : 'sri_rank_value',
        skip: 0,
        limit: BATCH_SIZE,
        searchterm: searchQuery
      });
      setTestRawData({
        records: data,
        totalFetched: data.length,
        lastBatchSize: data.length,
        currentSkip: BATCH_SIZE,
        status: data.length < BATCH_SIZE ? 'Complete - No more data' : 'Ready for append'
      });
    } catch (err) {
      console.error('Test API call failed:', err);
      setTestRawData({
        error: err.message,
        records: [],
        totalFetched: 0,
        currentSkip: 0,
        status: 'Error'
      });
    } finally {
      setIsTestLoading(false);
    }
  };

  const handleTestAppend = async () => {
    if (!testRawData || !testRawData.records) {
      alert('Please fetch first!');
      return;
    }
    setIsTestLoading(true);
    await new Promise(requestAnimationFrame);
    const BATCH_SIZE = 50;
    const currentSkip = testRawData.currentSkip || 0;
    try {
      // await delay(10000);
      const data = await fetchNewsletterData({
        filterdata: getFilterData(),
        sortvalue: sortBy === 'publish_date' ? 'published_date' : 'sri_rank_value',
        skip: currentSkip,
        limit: BATCH_SIZE,
        searchterm: searchQuery
      });
      const allRecordsTest = [...testRawData.records, ...data];
      setTestRawData({
        records: allRecordsTest,
        totalFetched: allRecordsTest.length,
        lastBatchSize: data.length,
        currentSkip: currentSkip + BATCH_SIZE,
        status: data.length < BATCH_SIZE ? 'Complete - No more data' : 'Ready for append'
      });
    } catch (err) {
      console.error('Test API append failed:', err);
      setTestRawData(prev => ({
        ...prev,
        error: err.message,
        status: 'Error'
      }));
    } finally {
      setIsTestLoading(false);
    }
  };

  return (
    <div className="app-container-news">
      <div style={{ marginLeft: '70px' }}>
        <Navbar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            sortBy={sortBy}
            onSortChange={setSortBy}
            selectedCount={selectedArticleIds.size}
            onBulkShare={handleBulkShare}
            onClearSelection={handleClearSelection}
          />
      </div>

      <div className="main-content-news">
        <div className="content-wrapper-news">
          <FilterSidebar
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            selectedNewsTypes={selectedNewsTypes}
            setSelectedNewsTypes={setSelectedNewsTypes}
            selectedRegions={selectedRegions}
            setSelectedRegions={setSelectedRegions}
            selectedCompanies={selectedCompanies}
            setSelectedCompanies={setSelectedCompanies}
            companyNamesMap={combinedCompanyNamesMap}
            user={user}
            onCompaniesReady={handleCompaniesReady}
            onCompanyNameMapReady={setSidebarCompanyMap}
            onlyWithTimeline={onlyWithTimeline}
            setOnlyWithTimeline={setOnlyWithTimeline}
          />

          <div className="articles-container-news">
            <InfoBanner
              // selectedCount={selectedArticleIds.size}  // moved to Navbar
              // onBulkShare={handleBulkShare}            // moved to Navbar
              // onClearSelection={handleClearSelection}  // moved to Navbar
              // sortBy={sortBy}                          // moved to Navbar
              // onSortChange={setSortBy}                 // moved to Navbar
              selectedCompanies={selectedCompanies}
              selectedNewsTypes={selectedNewsTypes}
              selectedRegions={selectedRegions}
              searchQuery={searchQuery}
              companyNames={combinedCompanyNamesMap}
              newsTypesMap={newsTypesMap}
              regionsMap={regionsMap}
              onlyWithTimeline={onlyWithTimeline}
              onRemoveFilter={handleRemoveFilter}
              onResetAll={handleResetAllFilters}
            />

            {/* Test Panel - Commented out
            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#1a1a2e', borderRadius: '8px', border: '1px solid #333' }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  onClick={handleTestFetch}
                  disabled={isTestLoading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: isTestLoading ? '#555' : '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: isTestLoading ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  {isTestLoading ? 'Loading...' : 'Fetch First 100'}
                </button>
                <button
                  onClick={handleTestAppend}
                  disabled={isTestLoading || !testRawData?.records?.length}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: (isTestLoading || !testRawData?.records?.length) ? '#555' : '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: (isTestLoading || !testRawData?.records?.length) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  Append Next 100
                </button>
              </div>

              {testRawData && (
                <div style={{ marginTop: '15px' }}>
                  <div style={{ display: 'flex', gap: '20px', marginBottom: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <h4 style={{ color: '#fff', margin: 0 }}>
                      Total Records: <span style={{ color: '#00ff88' }}>{testRawData.totalFetched || 0}</span>
                    </h4>
                    <span style={{ color: '#aaa' }}>
                      Batches: <span style={{ color: '#4CAF50' }}>{testRawData.batchesFetched || 0}</span>
                    </span>
                    <span style={{ color: '#aaa' }}>
                      Last Batch: <span style={{ color: '#4CAF50' }}>{testRawData.lastBatchSize || 0}</span> records
                    </span>
                    <span style={{
                      color: testRawData.status === 'Complete' ? '#00ff88' : testRawData.status === 'Error' ? '#ff4444' : '#ffaa00',
                      fontWeight: 'bold'
                    }}>
                      Status: {testRawData.status || 'Unknown'}
                    </span>
                  </div>
                  {testRawData.error && (
                    <p style={{ color: '#ff4444', marginBottom: '10px' }}>Error: {testRawData.error}</p>
                  )}
                  <pre style={{
                    backgroundColor: '#0d0d1a',
                    padding: '15px',
                    borderRadius: '5px',
                    color: '#00ff88',
                    fontSize: '12px',
                    maxHeight: '400px',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all'
                  }}>
                    {JSON.stringify(testRawData.records || testRawData, null, 2)}
                  </pre>
                </div>
              )}
            </div>
            */}

            {isLoading && (
              <div className="loading-container-news">
                <div className="loading-spinner-news"></div>
                <br />
                <p>Loading newsletter data...</p>
              </div>
            )}

            {error && !isLoading && (
              <div className="error-container-news">
                <p className="error-message-news">⚠️ {error}</p>
                <button
                  className="retry-button-news"
                  onClick={() => window.location.reload()}
                >
                  Retry
                </button>
              </div>
            )}

            {!isLoading && !error && (
              <>
                <div className={`articles-timeline-wrapper-news ${isTimelinePanelOpen ? 'timeline-open' : ''}`}>
                  <div className={`articles-grid-news ${selectedCompanies.length > 0 ||
                    selectedNewsTypes.length > 0 ||
                    selectedRegions.length > 0 ||
                    searchQuery
                    ? 'has-active-filters'
                    : ''
                    }`}>
                    <div className='row'>
                      {transformedArticles.map(article => {
                        const hasSummary = article.summary && article.summary.trim() !== '';
                        const articleVote = userVotes.get(article.id) || null;
                        const isVoting = votingArticleId === article.id;
                        return hasSummary ? (
                          <div className={`${isTimelinePanelOpen ? 'col-12' : 'col-md-6'}`} key={article.id}>
                            <NewsCard
                              key={article.id}
                              id={article.id}
                              title={article.title}
                              url={article.url}
                              summary={article.summary}
                              newsTypes={article.newsTypes}
                              alertLabel={article.alertLabel}
                              publishedDate={article.publishedDate}
                              companies={article.companies}
                              locations={article.locations}
                              sriRankValue={article.sriRankValue}
                              isSelected={selectedArticleIds.has(article.id)}
                              onSelect={handleToggleSelection}
                              userVote={articleVote}
                              onVote={handleVote}
                              isVoting={isVoting}
                              onOpenTimeline={handleOpenTimeline}
                              onFilterClick={handleFilterClick}
                              storylines={storylinesData}
                            />
                          </div>
                        ) : (
                          <div className={`${isTimelinePanelOpen ? 'col-12' : 'col-md-6'}`} key={article.id}>
                            <AICard
                              key={article.id}
                              id={article.id}
                              title={article.title}
                              url={article.url}
                              summary={article.summary}
                              newsTypes={article.newsTypes}
                              alertLabel={article.alertLabel}
                              publishedDate={article.publishedDate}
                              companies={article.companies}
                              locations={article.locations}
                              sriRankValue={article.sriRankValue}
                              isSelected={selectedArticleIds.has(article.id)}
                              onSelect={handleToggleSelection}
                              userVote={articleVote}
                              onVote={handleVote}
                              isVoting={isVoting}
                              onOpenTimeline={handleOpenTimeline}
                              onFilterClick={handleFilterClick}
                              storylines={storylinesData}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {isTimelinePanelOpen && (
                    <TimelinePanel
                      isOpen={isTimelinePanelOpen}
                      onClose={handleCloseTimeline}
                      articleId={timelineArticleId}
                      articleTitle={timelineArticleTitle}
                      articleUrl={timelineArticleUrl}
                      storylines={storylinesData}
                      hasActiveFilters={
                        selectedCompanies.length > 0 ||
                        selectedNewsTypes.length > 0 ||
                        selectedRegions.length > 0 ||
                        !!searchQuery
                      }
                    />
                  )}
                </div>

                <div ref={loadMoreRef} className="load-more-sentinel-news">
                  {isLoadingMore && (
                    <div className="loading-more-container-news">
                      <div className="loading-spinner-small-news"></div>
                      <p>Loading more articles...</p>
                    </div>
                  )}
                  {!hasMoreData && transformedArticles.length > 0 && (
                    <div className="end-of-list-news">
                      <p>You've reached the end! ({transformedArticles.length} articles loaded)</p>
                    </div>
                  )}
                </div>
              </>
            )}

            {!isLoading && !error && transformedArticles.length === 0 && (
              <div className="no-results-news">
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
                <h3>No articles found</h3>
                <p>We couldn't find any news matching your selected filters.</p>
                <p>Try adjusting your search or filters to see more results.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <BulkShareModal
        isOpen={isBulkShareModalOpen}
        onClose={handleBulkShareClose}
        selectedArticles={selectedArticles}
      />
    </div>
  );
};

export default NewsletterPage;